import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { apiFetch } from '@/lib/api'
import { formatEur } from '@/lib/format'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Analytics {
  overview: {
    totalMissions: number
    completedMissions: number
    openMissions: number
    assignedMissions: number
    cancelledMissions: number
    totalSpent: number
    avgAmount: number
    completionRate: number
  }
  byMonth: Array<{ month: string; count: number; spent: number }>
  byType: Array<{ type: string; count: number; spent: number }>
  byStatus: Array<{ status: string; count: number }>
  topProviders: Array<{
    id: string; firstName: string; lastName: string
    rating: number; count: number; earned: number
  }>
}

// ─── Constantes graphique ──────────────────────────────────────────────────────

const GRID_COLOR = 'rgba(255,255,255,0.06)'
const TICK_COLOR = '#6B6B80'
const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(10,10,20,0.95)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '12px',
  color: '#F0F0F5',
  fontSize: '12px',
  padding: '8px 12px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
}
const PIE_COLORS = ['#6E7FFF', '#4ADE80', '#FBBF24']
const TYPE_LABELS: Record<string, string> = { EVENT: 'Événement', TRAINING: 'Formation', OTHER: 'Autre' }
const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Ouverte', ASSIGNED: 'Assignée', IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminée', CANCELLED: 'Annulée',
}
const STATUS_COLORS: Record<string, string> = {
  OPEN: '#6E7FFF', ASSIGNED: '#4ADE80', IN_PROGRESS: '#FBBF24',
  COMPLETED: '#A8A8B8', CANCELLED: '#F87171',
}

// ─── Utilitaires ──────────────────────────────────────────────────────────────

function monthLabel(key: string): string {
  const [y, m] = key.split('-')
  return new Intl.DateTimeFormat('fr-FR', { month: 'short' })
    .format(new Date(Number(y), Number(m) - 1))
    .replace('.', '')
}

// ─── Composants ───────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, color = 'default',
}: { label: string; value: string | number; sub?: string; color?: 'default' | 'accent' | 'success' | 'warning' | 'danger' }) {
  const textColor = {
    default: 'text-text-primary',
    accent: 'text-[#8B9AFF]',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[color]

  return (
    <div className="glass-card p-5">
      <p className="text-text-muted text-xs mb-3">{label}</p>
      <p className={`text-3xl font-bold ${textColor} mb-1`}>{value}</p>
      {sub && <p className="text-text-muted text-xs">{sub}</p>}
    </div>
  )
}

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-sm font-semibold text-text-secondary mb-4">{children}</h2>
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const [data, setData] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    apiFetch<Analytics>('/companies/me/analytics')
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 glass-card animate-pulse w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="glass-card h-28 animate-pulse" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="glass-card h-64 animate-pulse" />
          <div className="glass-card h-64 animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-4xl mb-4">⚠️</p>
        <p className="text-text-primary mb-4">{error || 'Erreur de chargement'}</p>
        <button onClick={() => window.location.reload()} className="glass-btn px-5 py-2.5 text-sm rounded-xl">
          Réessayer
        </button>
      </div>
    )
  }

  if (data.overview.totalMissions === 0) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary">Analytics</h1>
          <p className="text-text-secondary text-sm mt-1">Statistiques de votre activité</p>
        </div>
        <div className="glass-card p-14 text-center">
          <p className="text-5xl mb-5">📊</p>
          <p className="text-text-primary font-semibold text-lg mb-2">Aucune donnée pour l'instant</p>
          <p className="text-text-secondary text-sm mb-6">
            Publiez des missions pour voir vos statistiques apparaître ici.
          </p>
          <Link to="/missions/nouvelle" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
            Créer ma première mission →
          </Link>
        </div>
      </div>
    )
  }

  const { overview, byMonth, byType, byStatus, topProviders } = data

  const monthData = byMonth.map((m) => ({
    ...m,
    label: monthLabel(m.month),
    spentEur: m.spent / 100,
  }))

  const typeData = byType.map((t) => ({
    name: TYPE_LABELS[t.type] ?? t.type,
    value: t.count,
    spent: t.spent,
  }))

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Analytics</h1>
        <p className="text-text-secondary text-sm mt-1">Vue d'ensemble de votre activité Velink</p>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total missions" value={overview.totalMissions} />
        <StatCard
          label="Taux de completion"
          value={`${overview.completionRate} %`}
          sub={`${overview.completedMissions} terminée${overview.completedMissions !== 1 ? 's' : ''}`}
          color="success"
        />
        <StatCard
          label="Total dépensé"
          value={formatEur(overview.totalSpent)}
          sub="missions terminées"
          color="accent"
        />
        <StatCard
          label="Budget moyen"
          value={formatEur(overview.avgAmount)}
          sub="par mission"
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">

        {/* Missions par mois */}
        <div className="glass-card p-6">
          <SectionTitle>Missions publiées — 6 derniers mois</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthData} barSize={32} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: TICK_COLOR, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: TICK_COLOR, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                formatter={(v: number) => [v, 'missions']}
              />
              <Bar dataKey="count" fill="#6E7FFF" fillOpacity={0.85} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Dépenses par mois */}
        <div className="glass-card p-6">
          <SectionTitle>Dépenses — 6 derniers mois</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={monthData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gradSpent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6E7FFF" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#6E7FFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: TICK_COLOR, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: TICK_COLOR, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${Math.round(v)} €`}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(v: number) => [`${v.toFixed(0)} €`, 'Dépenses']}
              />
              <Area
                type="monotone"
                dataKey="spentEur"
                stroke="#6E7FFF"
                strokeWidth={2}
                fill="url(#gradSpent)"
                dot={false}
                activeDot={{ r: 4, fill: '#8B9AFF', stroke: '#0A0A14', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">

        {/* Répartition par type */}
        <div className="glass-card p-6">
          <SectionTitle>Répartition par type</SectionTitle>
          {typeData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie
                    data={typeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {typeData.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} fillOpacity={0.85} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v: number, _: string, entry: { payload?: { name?: string } }) => [
                      `${v} mission${v !== 1 ? 's' : ''}`,
                      entry.payload?.name ?? '',
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {typeData.map((t, i) => (
                  <div key={t.name}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                        />
                        <span className="text-text-secondary text-sm">{t.name}</span>
                      </div>
                      <span className="text-text-primary font-medium text-sm">{t.value}</span>
                    </div>
                    {t.spent > 0 && (
                      <p className="text-text-muted text-xs ml-4">{formatEur(t.spent)} dépensés</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-text-muted text-sm">Aucune donnée</p>
          )}
        </div>

        {/* Statuts */}
        <div className="glass-card p-6">
          <SectionTitle>Répartition par statut</SectionTitle>
          <div className="space-y-3">
            {byStatus
              .filter((s) => s.count > 0)
              .map((s) => (
                <div key={s.status}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-text-secondary text-sm">
                      {STATUS_LABELS[s.status] ?? s.status}
                    </span>
                    <span className="text-text-primary text-sm font-medium">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.round((s.count / overview.totalMissions) * 100)}%`,
                        background: STATUS_COLORS[s.status] ?? '#6B6B80',
                        opacity: 0.8,
                      }}
                    />
                  </div>
                </div>
              ))}
          </div>

          {/* Mini stats */}
          <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/[0.08]">
            <div>
              <p className="text-text-muted text-xs mb-0.5">Missions actives</p>
              <p className="text-text-primary font-semibold">
                {overview.openMissions + overview.assignedMissions}
              </p>
            </div>
            <div>
              <p className="text-text-muted text-xs mb-0.5">Annulées</p>
              <p className="text-danger font-semibold">{overview.cancelledMissions}</p>
            </div>
          </div>
        </div>

      </div>

      {/* Top prestataires */}
      {topProviders.length > 0 && (
        <div className="glass-card p-6">
          <SectionTitle>Top prestataires</SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  {['Prestataire', 'Note', 'Missions', 'Montant versé'].map((h) => (
                    <th key={h} className="text-left text-xs text-text-muted font-medium pb-3 pr-6 last:pr-0">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topProviders.map((p, i) => (
                  <tr key={p.id} className="border-b border-white/[0.05] last:border-0">
                    <td className="py-3 pr-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-accent text-xs font-bold">
                            {p.firstName[0]}{p.lastName[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-text-primary text-sm font-medium">
                            {p.firstName} {p.lastName}
                          </p>
                          <p className="text-text-muted text-xs">#{i + 1}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-6">
                      {p.rating > 0 ? (
                        <>
                          <span className="text-warning text-sm">★</span>{' '}
                          <span className="text-text-primary text-sm">{p.rating.toFixed(1)}</span>
                        </>
                      ) : (
                        <span className="text-text-muted text-sm">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-6">
                      <span className="text-text-primary text-sm font-medium">{p.count}</span>
                      <span className="text-text-muted text-xs ml-1">
                        mission{p.count !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="py-3">
                      {p.earned > 0 ? (
                        <span className="text-success text-sm font-medium">{formatEur(p.earned)}</span>
                      ) : (
                        <span className="text-text-muted text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
